/**
 * Audit QMS Manager — List View
 * PRD: docs/PRD_Audit_Lifecycle_v2.md Section 7.1
 * 
 * Displays audits from the new 'audits' collection with lifecycle support.
 * Replaces AuditRecordManager.jsx in navigation.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Calendar, User, FileText, AlertCircle, Edit, Trash2 } from 'lucide-react';
import { auditServiceQMS, AUDIT_TYPES, AUDIT_QMS_STATUSES } from '../../services/auditService_qms';
import { useNotifications } from '../NotificationSystem';
import { useOrg } from '../../hooks/useOrg';
import './AuditQMSManager.css';

const AuditQMSManager = ({ userId, onNavigateToDetail }) => {
  const { t } = useTranslation(['qms', 'common']);
  const { notify } = useNotifications();
  const { activeOrgId: organizationId, isLoading: orgLoading } = useOrg();
  
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  useEffect(() => {
    loadAudits();
  }, [organizationId, statusFilter, typeFilter, dateFromFilter, dateToFilter]);

  const loadAudits = async () => {
    if (!organizationId) return;
    
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (typeFilter) filters.auditType = typeFilter;
      if (dateFromFilter) filters.dateFrom = dateFromFilter;
      if (dateToFilter) filters.dateTo = dateToFilter;
      
      const data = await auditServiceQMS.getByOrganization(organizationId, filters);
      setAudits(data);
    } catch (err) {
      console.error('[AuditQMSManager] Load failed:', err);
      setError(err.message);
      setAudits([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    onNavigateToDetail?.('new');
  };

  const handleViewAudit = (auditId) => {
    onNavigateToDetail?.(auditId);
  };

  const handleDeleteAudit = async (auditId, e) => {
    e?.stopPropagation();
    
    if (!window.confirm(t('qms:confirmDeleteAudit') || 'Are you sure you want to delete this audit? This action cannot be undone.')) {
      return;
    }
    
    try {
      await auditServiceQMS.delete(auditId, userId);
      notify.success(t('qms:auditDeleted') || 'Audit deleted successfully');
      loadAudits();
    } catch (err) {
      console.error('[AuditQMSManager] Delete failed:', err);
      notify.error(err.message);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('ro-RO');
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      planificat: 'status-badge-planificat',
      in_desfasurare: 'status-badge-in-desfasurare',
      constatari: 'status-badge-constatari',
      inchis: 'status-badge-inchis',
      redeschis: 'status-badge-redeschis'
    };
    return classes[status] || 'status-badge-default';
  };

  const getStatusLabel = (status) => {
    return t(`qms:auditStatus.${status}`) || status;
  };

  const getTypeLabel = (type) => {
    return t(`qms:auditType_${type}`) || type;
  };

  if (orgLoading) {
    return (
      <div className="audit-qms-manager">
        <div className="aqm-header">
          <h2>{t('common:loading') || 'Loading...'}</h2>
        </div>
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="audit-qms-manager">
        <p className="aqm-subtitle" role="status">
          {t('qms:noOrganizationContext') || 'Selectează o organizație pentru a vedea auditurile.'}
        </p>
      </div>
    );
  }

  return (
    <div className="audit-qms-manager">
      <div className="aqm-header">
        <div className="aqm-header-left">
          <h2>{t('qms:auditRecords') || 'Audit Records'}</h2>
          <p className="aqm-subtitle">
            {t('qms:auditLifecycleDescription') || 'Manage audits with full lifecycle: planning, execution, findings, and closure'}
          </p>
        </div>
        <button 
          type="button" 
          className="btn btn-primary"
          onClick={handleCreateNew}
        >
          <Plus size={18} />
          {t('qms:createAudit') || 'Create Audit'}
        </button>
      </div>

      <div className="aqm-filters">
        <div className="filter-group">
          <label>{t('qms:status') || 'Status'}</label>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">{t('qms:allStatuses') || 'All statuses'}</option>
            {AUDIT_QMS_STATUSES.map((status) => (
              <option key={status} value={status}>{getStatusLabel(status)}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>{t('qms:auditType') || 'Type'}</label>
          <select 
            value={typeFilter} 
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">{t('qms:allTypes') || 'All types'}</option>
            {AUDIT_TYPES.map((type) => (
              <option key={type} value={type}>{getTypeLabel(type)}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>{t('qms:dateFrom') || 'From'}</label>
          <input 
            type="date" 
            value={dateFromFilter}
            onChange={(e) => setDateFromFilter(e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>{t('qms:dateTo') || 'To'}</label>
          <input 
            type="date" 
            value={dateToFilter}
            onChange={(e) => setDateToFilter(e.target.value)}
            className="filter-input"
          />
        </div>

        {(statusFilter || typeFilter || dateFromFilter || dateToFilter) && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setStatusFilter('');
              setTypeFilter('');
              setDateFromFilter('');
              setDateToFilter('');
            }}
          >
            {t('common:clearFilters') || 'Clear filters'}
          </button>
        )}
      </div>

      {error && (
        <div className="aqm-error">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {loading ? (
        <div className="aqm-loading">{t('common:loading') || 'Loading...'}</div>
      ) : audits.length === 0 ? (
        <div className="aqm-empty">
          <FileText size={48} className="empty-icon" />
          <p>{t('qms:noAuditsYet') || 'No audits found'}</p>
          <p className="empty-hint">
            {t('qms:createFirstAudit') || 'Create your first audit to start tracking compliance verification'}
          </p>
          <button 
            type="button" 
            className="btn btn-primary"
            onClick={handleCreateNew}
          >
            <Plus size={18} />
            {t('qms:createAudit') || 'Create Audit'}
          </button>
        </div>
      ) : (
        <div className="aqm-table-container">
          <table className="aqm-table">
            <thead>
              <tr>
                <th>{t('qms:title') || 'Title'}</th>
                <th>{t('qms:type') || 'Type'}</th>
                <th>{t('qms:organizationalScope') || 'Scope'}</th>
                <th>{t('qms:plannedDate') || 'Planned Date'}</th>
                <th>{t('qms:auditor') || 'Auditor'}</th>
                <th>{t('qms:status') || 'Status'}</th>
                <th>{t('qms:instruments') || 'Instruments'}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {audits.map((audit) => (
                <tr 
                  key={audit.id} 
                  className="aqm-row"
                  onClick={() => handleViewAudit(audit.id)}
                >
                  <td className="aqm-title">
                    <FileText size={16} className="row-icon" />
                    {audit.title}
                  </td>
                  <td>
                    <span className="type-badge">
                      {getTypeLabel(audit.auditType)}
                    </span>
                  </td>
                  <td className="aqm-scope">
                    {audit.organizationalScope?.nodeLabel || '—'}
                  </td>
                  <td>
                    <div className="date-cell">
                      <Calendar size={14} />
                      {formatDate(audit.plannedDate)}
                    </div>
                  </td>
                  <td>
                    <div className="auditor-cell">
                      <User size={14} />
                      {audit.auditorName || '—'}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(audit.status)}`}>
                      {getStatusLabel(audit.status)}
                    </span>
                  </td>
                  <td className="aqm-instruments">
                    {audit.assessmentInstances?.length || 0}
                  </td>
                  <td className="aqm-actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewAudit(audit.id);
                      }}
                      title={t('common:edit') || 'Edit'}
                    >
                      <Edit size={16} />
                    </button>
                    {audit.status === 'planificat' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={(e) => handleDeleteAudit(audit.id, e)}
                        title={t('common:delete') || 'Delete'}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditQMSManager;
